import {
  ClaimItemProposalDTOSettledSourceEnum,
  ClaimProposalDTOSourceEnum,
  ClaimProposalDTOStatusEnum,
} from '@reposit/api-client';
import { sortBy } from 'lodash';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import LandlordIcon from '../../../assets/svg/person-active.svg';
import TenantsIcon from '../../../assets/svg/tenants.svg';
import { getBreakpoint } from '../../../base/style';
import { ItemProposal } from '../../../redux/selectors/mediation.selectors';
import { penceToPounds } from '../../../utils/common.utils';
import { Header4, P2 } from '../../Typography';
import { isProposalPublished } from './AuditTrail';

export const AGENT_FIRST_ROUND = 1;
export const TENANT_ROUND = 2;
export const AGENT_FINAL_ROUND = 3;

interface HistoryProps {
  itemProposals: ItemProposal[];
}

interface HistoryItemProps {
  itemProposal: ItemProposal;
}

const HistoryItemContainer = styled.div`
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: solid 1px lightgrey;
  &:last-child {
    border-bottom: solid 1px white;
  }
`;

export const HistoryContainer = styled.div``;

const BoldText = styled.span`
  font-weight: bold;
`;

const ItalicsText = styled.span`
  font-style: italic;
`;

const Icon = styled.img`
  height: 20px;
  width: 20px;
  margin-bottom: 6px;
  margin-right: 10px;
`;

const IconAndHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media screen and (min-width: ${getBreakpoint('md')}) {
    flex-direction: row;
    align-items: center;
  }
`;

const Timestamp = styled.span`
  font-weight: normal;
`;

const getIcon = (source: ClaimProposalDTOSourceEnum) => {
  if (source === ClaimProposalDTOSourceEnum.SUPPLIER) {
    return LandlordIcon;
  } else {
    return TenantsIcon;
  }
};

export const HistoryItem: React.FC<HistoryItemProps> = ({ itemProposal }) => {
  const { settled: itemSettled, settledSource, claimProposal } = itemProposal;
  const proposalPublishedAt = claimProposal.publishedAt;
  const proposalSettled = claimProposal.status === ClaimProposalDTOStatusEnum.SETTLED;
  const proposalAwaitingPayment = claimProposal.status === ClaimProposalDTOStatusEnum.AWAITINGPAYMENT;
  const supplierCreatedCharge = 'The agent/landlord created a charge of ';
  const tenantProposed = 'The tenant(s) proposed ';
  const tenantAcceptedCharge = 'The tenant(s) accepted the charge of ';
  const supplierAcceptedProposal = 'The agent/landlord accepted the tenant(s) proposal of';
  const supplierFinalProposal = 'The agent/landlord put forward a final proposal of ';
  const leadingText = (round: number) => {
    if (round === AGENT_FIRST_ROUND) {
      if (itemSettled && !proposalAwaitingPayment) {
        return tenantAcceptedCharge;
      }
      return supplierCreatedCharge;
    } else if (round === TENANT_ROUND) {
      if (itemSettled) {
        if (settledSource === ClaimItemProposalDTOSettledSourceEnum.SUPPLIER) {
          return supplierAcceptedProposal;
        }
        return tenantAcceptedCharge;
      }
      return tenantProposed;
    } else if (round === AGENT_FINAL_ROUND) {
      if (itemSettled) {
        if (settledSource === ClaimItemProposalDTOSettledSourceEnum.SUPPLIER) {
          return supplierAcceptedProposal;
        } else if (proposalSettled) {
          return tenantAcceptedCharge;
        }
      }
      return supplierFinalProposal;
    }
  };

  return (
    <HistoryItemContainer>
      <IconAndHeaderContainer>
        <Icon src={getIcon(itemProposal.claimProposal.source)} />
        <Header4 style={{ marginBottom: '6px' }}>
          {leadingText(itemProposal.claimProposal.round)} £{penceToPounds(itemProposal.amount)}{' '}
          <Timestamp>({moment(proposalPublishedAt).format('DD/MM/YYYY')})</Timestamp>
        </Header4>
      </IconAndHeaderContainer>

      {itemProposal.explanation ? (
        <P2 style={{ margin: 0 }}>
          <BoldText>Reasoning:</BoldText> <ItalicsText>{itemProposal.explanation}</ItalicsText>
        </P2>
      ) : null}
    </HistoryItemContainer>
  );
};

const filterOutUnpublishedProposals = (ip: ItemProposal): boolean => {
  const proposal = ip.claimProposal;
  return isProposalPublished(proposal);
};

const isProposalSettledBySupplierInTenantRound = (ip: ItemProposal) => {
  return (
    ip.settled &&
    ip.settledSource === ClaimItemProposalDTOSettledSourceEnum.SUPPLIER &&
    ip.claimProposal.round === TENANT_ROUND &&
    ip.claimProposal.status === ClaimProposalDTOStatusEnum.SETTLED
  );
};

const isItemSettledByTenantInAgentRound = (ip: ItemProposal) => {
  return (
    ip.settled &&
    ip.settledSource === ClaimItemProposalDTOSettledSourceEnum.TENANT &&
    (ip.claimProposal.round === AGENT_FINAL_ROUND || ip.claimProposal.round === AGENT_FIRST_ROUND) &&
    ip.claimProposal.status === ClaimProposalDTOStatusEnum.SETTLED
  );
};

export const History: React.FC<HistoryProps> = ({ itemProposals }) => {
  const sortedItemProposals = sortBy(itemProposals, (ip) => ip.createdAt)
    .filter(filterOutUnpublishedProposals)
    .filter((ip, i) => {
      // filter out any previously settled items
      const prev = itemProposals[i - 1];
      if (!prev) return true;
      if (prev.settled) return false;
      return true;
    })
    .reverse();
  return (
    <HistoryContainer>
      {sortedItemProposals.map((sip) => {
        // deals with one proposal handling one party accepting and one party proposing
        if (isItemSettledByTenantInAgentRound(sip) || isProposalSettledBySupplierInTenantRound(sip)) {
          return (
            <>
              {/* First item is the acceptance, second is the original proposal */}
              <HistoryItem itemProposal={{ ...sip, explanation: undefined }} />
              <HistoryItem itemProposal={{ ...sip, settled: false, settledSource: undefined, updatedAt: sip.createdAt }} />
            </>
          );
        } else {
          return <HistoryItem itemProposal={sip} />;
        }
      })}
    </HistoryContainer>
  );
};
